import { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Typography, Flex } from 'antd';
import { CustomFormInput } from '../../../components';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import CustomFormErrorMessage from '../../../components/CustomFormErrorMessage/CustomFormErrorMessage';
import { sanitizeInput } from '../../../utilities/commonFunctions';
const passwordRules = {
  length: {
    regex: /^.{8,12}$/,
    message: 'must be between 8 and 12 characters',
  },
  lowercase: { regex: /[a-z]/, message: 'lowercase letters (a-z)' },
  uppercase: { regex: /[A-Z]/, message: 'uppercase letters (A-Z)' },
  number: { regex: /\d/, message: 'at least one number (0-9)' },
  special: {
    regex: /^(?=.*[!@#$%^&*(),.?":{}|<>_[\]+=]).*$/,
    message: 'a special character (&,*#)',
  },
};

const { Title, Text } = Typography;

const CreateNewPassword = ({
  form,
  role = '',
  title = 'Create a new password',
  style = {},
  titleLevel = 4,
}) => {
  const [validationStatus, setValidationStatus] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false,
  });
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [password, setPassword] = useState('');
  const [, setConfirmPassword] = useState('');

  const onPasswordChange = e => {
    const value = sanitizeInput(e.target.value);
    setPassword(value);
    setValidationStatus({
      length: passwordRules.length.regex.test(value),
      lowercase: passwordRules.lowercase.regex.test(value),
      uppercase: passwordRules.uppercase.regex.test(value),
      number: passwordRules.number.regex.test(value),
      special: passwordRules.special.regex.test(value),
    });
  };
  const validatePassword = (_, value) => {
    const validations = Object.keys(passwordRules).map(key =>
      passwordRules[key].regex.test(value)
    );
    if (validations.every(v => v) || !value) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('Check password criteria'));
  };

  const onConfirmPasswordChange = e => {
    const value = sanitizeInput(e.target.value);
    setConfirmPassword(value);
  };
  const validateConfirmPassword = (_, value) => {
    if (!value || form.getFieldValue('new_password') === value) {
      setConfirmPasswordError('');
      return Promise.resolve();
    }
    setConfirmPasswordError('Passwords do not match');
    return Promise.reject(new Error('Passwords do not match'));
  };

  return (
    <Flex
      vertical
      style={{ paddingTop: role === 'admin' && 24 }}
      className="full-width"
    >
      {role !== 'super_admin' && role !== 'change-password' && (
        <Title level={titleLevel} style={style}>
          {title}
        </Title>
      )}
      <CustomFormInput
        label="New Password"
        placeholder=""
        name="new_password"
        rules={[
          {
            required: true,
            message: (
              <CustomFormErrorMessage message="Please input your password" />
            ),
          },
          { validator: validatePassword },
        ]}
        validateStatus={
          !password || Object.values(validationStatus).every(status => status)
            ? 'success'
            : 'error'
        }
        help={
          password &&
          !Object.values(validationStatus).every(status => status) ? (
            <CustomFormErrorMessage
              message="Check password criteria"
              className="form-info-container"
            />
          ) : null
        }
        isPassword
        onChange={onPasswordChange}
        value={password}
      />
      <Form.Item>
        <Text strong style={{ fontSize: '14px' }}>
          Create a password that contains
        </Text>
        <ul className="password-criteria">
          {Object.keys(passwordRules).map(key => (
            <li
              style={{ fontSize: '14px' }}
              key={key}
              className={
                !password ? 'text' : validationStatus[key] ? 'valid' : 'invalid'
              }
            >
              {password &&
                (validationStatus[key] ? (
                  <CheckOutlined
                    height="12px"
                    width="12px"
                    style={{ color: 'green' }}
                  />
                ) : (
                  <CloseOutlined
                    height="12px"
                    width="12px"
                    style={{ color: 'red' }}
                  />
                ))}
              {` ${passwordRules[key].message}`}
            </li>
          ))}
        </ul>
      </Form.Item>

      <CustomFormInput
        label="Confirm Password"
        placeholder=""
        name="confirm_password"
        rules={[
          {
            required: true,
            message: (
              <CustomFormErrorMessage message="Please confirm new password" />
            ),
          },
          { validator: validateConfirmPassword },
        ]}
        help={
          confirmPasswordError ? (
            <CustomFormErrorMessage
              message={confirmPasswordError}
              className="form-info-container"
            />
          ) : null
        }
        isPassword
        onChange={onConfirmPasswordChange}
      />
    </Flex>
  );
};

CreateNewPassword.propTypes = {
  form: PropTypes.object,
  role: PropTypes.string,
  title: PropTypes.string,
  style: PropTypes.object,
  titleLevel: PropTypes.number,
};

export default CreateNewPassword;
